import { useState } from "react";
import { apiClient } from "../lib/apiClient";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { TagSelector } from "./TagSelector";
import { EyeOff, CheckCircle2, Tag as TagIcon, Trash2, Loader2 } from "lucide-react";


/**
 * Action bar shown when the user has entered bulk-select mode.
 * Shared between DashboardPage and GroupPage.
 *
 * Props:
 *   selectedIds: string[] — currently-selected prayer IDs
 *   onCompleted: () => void — called after any bulk action succeeds (triggers refetch + exit select mode)
 *   onCancel: () => void — called when user clicks Cancel
 *   testIdPrefix: string — prefix for data-testid attributes (e.g. "bulk" or "bulk-group")
 */
export function BulkActionsBar({ selectedIds, onCompleted, onCancel, testIdPrefix = "bulk" }) {
  const count = selectedIds.length;
  const [busy, setBusy] = useState(null); // 'hide' | 'answer' | 'delete' | 'tags' | null
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [newTags, setNewTags] = useState([]);

  const runAction = async (action, endpointName, payload = {}) => {
    if (count === 0 || busy) return;
    setBusy(action);
    try {
      await apiClient.post(endpointName, { prayer_ids: selectedIds, ...payload });
      onCompleted();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${action} prayers`);
    } finally {
      setBusy(null);
    }
  };

  const handleHide = () => runAction("hide", "prayersBulkHide");
  const handleAnswer = () => runAction("mark answered", "prayersBulkAnswer");
  const handleDelete = () => {
    setDeleteConfirmOpen(false);
    runAction("delete", "prayersBulkDelete");
  };
  const handleApplyTags = () => {
    setTagDialogOpen(false);
    runAction("update tags on", "prayersBulkTags", { tags: newTags });
  };

  return (
    <>
      <span className="text-xs text-stone-dark/60 dark:text-stone-light/60 hidden sm:inline">
        {count} selected
      </span>
      <Button
        variant="outline"
        size="sm"
        className="rounded-full border-sage/40 text-sage hover:bg-sage/10"
        onClick={handleAnswer}
        disabled={count === 0 || busy}
        data-testid={`${testIdPrefix}-answer-btn`}
      >
        {busy === "mark answered" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        <span className="ml-1 hidden sm:inline">Answered</span>
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="rounded-full border-stone text-stone-dark/70 hover:bg-stone/30 dark:text-stone-light/70 dark:hover:bg-stone-dark/30"
        onClick={() => { setNewTags([]); setTagDialogOpen(true); }}
        disabled={count === 0 || busy}
        data-testid={`${testIdPrefix}-tags-btn`}
      >
        <TagIcon className="w-4 h-4" />
        <span className="ml-1 hidden sm:inline">Tags</span>
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="rounded-full border-stone text-stone-dark/70 hover:bg-stone/30 dark:text-stone-light/70 dark:hover:bg-stone-dark/30"
        onClick={handleHide}
        disabled={count === 0 || busy}
        data-testid={`${testIdPrefix}-hide-btn`}
      >
        {busy === "hide" ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />}
        <span className="ml-1 hidden sm:inline">Hide</span>
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
        onClick={() => setDeleteConfirmOpen(true)}
        disabled={count === 0 || busy}
        data-testid={`${testIdPrefix}-delete-btn`}
      >
        <Trash2 className="w-4 h-4" />
        <span className="ml-1 hidden sm:inline">Delete</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="rounded-full"
        onClick={onCancel}
        disabled={busy}
        data-testid={`${testIdPrefix}-cancel-btn`}
      >
        Cancel
      </Button>

      {/* Tag picker dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent data-testid={`${testIdPrefix}-tags-dialog`}>
          <DialogHeader>
            <DialogTitle>Update tags on {count} prayer{count === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>
              These tags will <strong>replace</strong> the current tag list on every selected prayer. Leave empty to clear all tags.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <TagSelector selectedTags={newTags} onChange={setNewTags} testIdPrefix={`${testIdPrefix}-tags-picker`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={handleApplyTags}
              className="bg-sage hover:bg-sage-dark text-white rounded-full"
              data-testid={`${testIdPrefix}-tags-apply-btn`}
            >
              Apply Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent data-testid={`${testIdPrefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {count} prayer{count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The selected prayers will be permanently removed for you and everyone in the group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`${testIdPrefix}-delete-confirm-btn`}
            >
              Delete {count}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
